import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Chip, Card, CardContent, Grid, TablePagination, useTheme
} from '@mui/material';
import { LocalShipping as Icon, Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';

const fetchPedidos = async () => {
  const res = await fetch('/api/pedidos', {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });
  return res.json();
};

const ESTADO_COLORS = {
  cotizacion: 'default',
  pendiente: 'warning',
  asignado: 'info',
  recogido: 'primary',
  en_camino: 'info',
  entregado: 'success',
  cancelado: 'error',
};

export default function Pedidos() {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data } = useQuery('pedidos', fetchPedidos, { refetchInterval: 15000 });
  const pedidos = data?.data || [];

  const pendientes = pedidos.filter(p => p.estado === 'pendiente');
  const enCurso = pedidos.filter(p => ['asignado', 'recogido', 'en_camino'].includes(p.estado));
  const entregadosHoy = pedidos.filter(p => {
    if (p.estado !== 'entregado') return false;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    return new Date(p.updatedAt) >= hoy;
  });

  const filtered = pedidos.filter(p =>
    !search ||
    `#${p.id}`.includes(search) ||
    p.cliente?.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    p.mensajero?.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    p.origen_direccion?.toLowerCase().includes(search.toLowerCase()) ||
    p.destino_direccion?.toLowerCase().includes(search.toLowerCase()) ||
    p.estado?.includes(search)
  );

  const pageData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Pedidos</Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Total</Typography>
          <Typography variant="h5">{pedidos.length}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Pendientes</Typography>
          <Typography variant="h5" color="warning.main">{pendientes.length}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">En Curso</Typography>
          <Typography variant="h5" color="info.main">{enCurso.length}</Typography>
        </CardContent></Card></Grid>
        <Grid item xs={6} sm={3}><Card><CardContent sx={{ py: 1.5 }}>
          <Typography color="textSecondary" variant="caption">Entregados Hoy</Typography>
          <Typography variant="h5" color="success.main">{entregadosHoy.length}</Typography>
        </CardContent></Card></Grid>
      </Grid>

      <TextField
        placeholder="Buscar pedidos..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
        sx={{ mb: 2, width: 360 }}
        InputProps={{ startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary, mr: 1, fontSize: 20 }} /> }}
        size="small"
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Cliente</strong></TableCell>
              <TableCell><strong>Mensajero</strong></TableCell>
              <TableCell><strong>Origen</strong></TableCell>
              <TableCell><strong>Destino</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Costo</strong></TableCell>
              <TableCell><strong>Pago</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageData.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><strong>#{p.id}</strong></TableCell>
                <TableCell>{p.cliente?.nombre || '—'}</TableCell>
                <TableCell>{p.mensajero?.nombre || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.origen_direccion || '—'}
                </TableCell>
                <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.destino_direccion || '—'}
                </TableCell>
                <TableCell>
                  <Chip label={p.tipo_servicio || '—'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip label={p.estado} size="small" color={ESTADO_COLORS[p.estado] || 'default'} />
                </TableCell>
                <TableCell>${parseFloat(p.costo_envio || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip label={p.pagado ? 'Pagado' : 'Pendiente'} size="small"
                    color={p.pagado ? 'success' : 'warning'} variant={p.pagado ? 'filled' : 'outlined'} />
                </TableCell>
                <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleString('es') : '—'}</TableCell>
              </TableRow>
            ))}
            {pageData.length === 0 && (
              <TableRow><TableCell colSpan={10} align="center"><Typography color="textSecondary">Sin resultados</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage} rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>
    </Box>
  );
}
